import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone support and set to Australia/Melbourne
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Australia/Melbourne");

const BookingCalendar = forwardRef(({
  bookedDates = [],
  blockedDates = [],
  onDateSelect,
  selectedDate: controlledSelectedDate = null,
  disableBookedDates = true,
  showEventMarkers = true,
  customStyles = {},
  filterType = "all",
  onDateClick,
  showRefreshButton = true,
  onRefresh,
  showLegend = true,
  maxDate: propMaxDate = null,
}, ref) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs.tz());
  const [bookedDatesSet, setBookedDatesSet] = useState(new Set());
  const [blockedDatesSet, setBlockedDatesSet] = useState(new Set());
  const [eventMarkers, setEventMarkers] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [internalSelectedDate, setInternalSelectedDate] = useState(null);
  const [cutoffPassed, setCutoffPassed] = useState(false);
  const maxDate = propMaxDate || dayjs.tz().add(1, 'year').format('YYYY-MM-DD');

  // NEW: state to store donor details per date
  const [bookedDetailsMap, setBookedDetailsMap] = useState({});

  const selectedDate = controlledSelectedDate || internalSelectedDate;

  useImperativeHandle(ref, () => ({
    refresh: () => {
      if (onRefresh) {
        return handleRefresh();
      }
      return Promise.resolve();
    },
    getBookedDates: () => bookedDatesSet,
    getBlockedDates: () => blockedDatesSet,
    getBookedDetails: () => bookedDetailsMap,
  }));

  const extractDateKey = (dateString) => {
    try {
      if (!dateString) return null;

      if (typeof dateString === 'string') {
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString;
        }
        if (dateString.includes('T')) {
          return dateString.split('T')[0];
        }
      }

      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      return null;
    } catch (error) {
      console.error("Error extracting date key:", error);
      return null;
    }
  };

  const getMarkerColor = (booking) => {
    const status = (booking.status || '').toLowerCase();

    if (status === 'approved') {
      return "green";
    } else if (status === 'pending') {
      return "orange";
    } else if (status === 'rejected') {
      return "red";
    }

    return "gray";
  };

  useEffect(() => {
    const processExternalData = () => {
      try {
        const bookedSet = new Set();
        const blockedSet = new Set();
        const markers = {};
        const detailsMap = {};

        bookedDates.forEach((booking) => {
          const eventDate = booking.dana_event_date || booking.date;
          if (eventDate) {
            const dateKey = extractDateKey(eventDate);
            if (dateKey) {
              if (!filterType || filterType === 'all' || booking.meal_type === filterType) {
                const status = (booking.status || '').toLowerCase();
                const shouldShow = status === 'approved' || status === 'pending' || status === 'completed';

                if (shouldShow) {
                  bookedSet.add(dateKey);

                  const donorFirstName = booking.member?.first_name || booking.donor_first_name || booking.first_name || 'Donor'
                  detailsMap[dateKey] = {
                    donorFirstName,
                    status,
                  };

                  if (showEventMarkers) {
                    markers[dateKey] = getMarkerColor(booking);
                  }
                }
              }
            }
          }
        });

        blockedDates.forEach((blocked) => {
          const eventDate = blocked.dana_event_date || blocked.date;
          if (eventDate) {
            const dateKey = extractDateKey(eventDate);
            if (dateKey) {
              if (!filterType || filterType === 'all' || blocked.meal_type === filterType) {
                blockedSet.add(dateKey);
              }
            }
          }
        });

        setBookedDatesSet(bookedSet);
        setBlockedDatesSet(blockedSet);
        setEventMarkers(markers);
        setBookedDetailsMap(detailsMap);
        setError(null);
      } catch (err) {
        console.error("Error processing calendar data:", err);
        setError("Failed to process calendar data.");
      }
    };

    processExternalData();
  }, [bookedDates, blockedDates, filterType, showEventMarkers]);

  useEffect(() => {
    const checkCutoff = () => {
      if (filterType === "breakfast") {
        const now = dayjs.tz();
        const cutoff = dayjs.tz().hour(6).minute(0).second(0);
        setCutoffPassed(now.isAfter(cutoff));
      } else if (filterType === "lunch") {
        const now = dayjs.tz();
        const cutoff = dayjs.tz().hour(11).minute(0).second(0);
        setCutoffPassed(now.isAfter(cutoff));
      } else {
        setCutoffPassed(false);
      }
    };

    checkCutoff();
    const interval = setInterval(checkCutoff, 30000);

    return () => clearInterval(interval);
  }, [filterType]);

  const handleRefresh = async () => {
    if (onRefresh) {
      setRefreshing(true);
      try {
        await onRefresh();
      } catch (err) {
        console.error("Error refreshing:", err);
        setError("Failed to refresh data.");
      } finally {
        setRefreshing(false);
      }
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, "month"));
  };

  const isPastDate = (dateString) => {
    const today = dayjs.tz().startOf('day');
    const date = dayjs.tz(dateString).startOf('day');
    return date.isBefore(today);
  };

  const isBeyondMaxDate = (dateString) => {
    const max = dayjs.tz(maxDate).startOf('day');
    const date = dayjs.tz(dateString).startOf('day');
    return date.isAfter(max, 'day');
  };

  const isTodayPastCutoff = () => cutoffPassed;

  const handleDateClick = (dateString) => {
    if (isPastDate(dateString)) {
      return;
    }

    if (bookedDatesSet.has(dateString)) {
      return;
    }

    if (blockedDatesSet.has(dateString)) {
      return;
    }

    if (onDateClick) {
      onDateClick(dateString, filterType);
    }

    if (!controlledSelectedDate) {
      setInternalSelectedDate(dateString);
    }

    if (onDateSelect) {
      onDateSelect(dateString, dayjs.tz(dateString));
    }
  };

  const isDateBooked = (dateString) => {
    return bookedDatesSet.has(dateString);
  };

  const isDateBlocked = (dateString) => {
    return blockedDatesSet.has(dateString);
  };

  const isDateDisabled = (dateString) => {
    if (isPastDate(dateString)) return true;
    if (isBeyondMaxDate(dateString)) return true;
    if (isDateBooked(dateString) || isDateBlocked(dateString)) return true;
    if (dayjs.tz(dateString).isSame(dayjs.tz(), "day") && isTodayPastCutoff()) return true;
    return false;
  };

const generateCalendarDays = () => {
  const startOfMonth = currentMonth.startOf("month");
  const endOfMonth = currentMonth.endOf("month");
  const startOfCalendar = startOfMonth.startOf("week");
  const endOfCalendar = endOfMonth.endOf("week");

  const days = [];
  let currentDay = startOfCalendar;

  while (
    currentDay.isBefore(endOfCalendar) ||
    currentDay.isSame(endOfCalendar, "day")
  ) {
    days.push(currentDay);
    currentDay = currentDay.add(1, "day");
  }

  // Pad to 42 days (6 rows)
  while (days.length < 42) {
    const lastDay = days[days.length - 1];
    days.push(lastDay.add(1, "day"));
  }

  return days;
};

  const getMarkerStyle = (color) => {
    const colorMap = {
      green: "#00C853",
      orange: "#FF6D00",
      red: "#D32F2F",
      gray: "#757575",
    };

    return colorMap[color] || colorMap.gray;
  };

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const calendarDays = generateCalendarDays();

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" action={
          <button onClick={handleRefresh} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            Retry
          </button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        padding: "18px",
        backgroundColor: "#fff",
        ...customStyles,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} width="100%">
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={handlePrevMonth}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ minWidth: "200px", textAlign: "center" }}
          >
            {currentMonth.format("MMMM YYYY")}
          </Typography>
          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        {showRefreshButton && onRefresh && (
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            title="Refresh calendar"
            sx={{
              animation: refreshing ? 'spin 0.5s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {refreshing && (
        <Box display="flex" justifyContent="center" mb={2}>
          <CircularProgress size={24} />
          <Typography variant="body2" ml={1}>
            Refreshing...
          </Typography>
        </Box>
      )}

      {/* Grid container – now with position: relative to anchor the overlay */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0,
          width: "364px",
          height: "364px",
          overflow: 'visible',
          position: 'relative', // anchor for the overlay
        }}
      >
        {weekDays.map((day, index) => (
          <Box
            key={day}
            sx={{
              width: "52px",
              height: "52px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              fontFamily: "Arial",
              fontSize: "14px",
              color: "black",
              margin: 0,
              padding: "8px",
              marginLeft: index > 0 ? "-1px" : 0,
              position: "relative",
              overflow: "visible",
            }}
          >
            {day}
          </Box>
        ))}

        {calendarDays.map((day) => {
          const dateString = day.format("YYYY-MM-DD");
          const isBooked = bookedDatesSet.has(dateString);
          const isBlocked = blockedDatesSet ? blockedDatesSet.has(dateString) : false;
          const isPast = isPastDate(dateString);
          const isDisabled = isDateDisabled(dateString);
          const marker = eventMarkers[dateString];
          const isSelected = selectedDate === dateString;

          let backgroundColor = "#fff";
          let color = "black";
          let borderStyle = "1px solid #ddd";
          let cursor = "pointer";

          if (isSelected) {
            backgroundColor = "#c49b36";
            color = "white";
          } else if (isPast || isBeyondMaxDate(dateString) || (dayjs.tz(dateString).isSame(dayjs.tz(), "day") && isTodayPastCutoff())) {
            backgroundColor = "#f0f0f0";
            color = "#ccc";
            cursor = "not-allowed";
          } else if (isBlocked) {
            backgroundColor = "#ffebee";
            color = "#d32f2f";
            cursor = "not-allowed";
          } else if (isBooked) {
            backgroundColor = "#fff3e0";
            color = "#666";
            cursor = "not-allowed";
            borderStyle = "2px solid #f44336";
          }

          if (isDisabled && !isPast) {
            cursor = "not-allowed";
          }

          return (
            <Box
              key={dateString}
              onClick={() => !isDisabled && handleDateClick(dateString)}
              sx={{
                width: "52px",
                height: "52px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: 'visible',
                zIndex: 1,
                cursor: cursor,
                border: borderStyle,
                borderRadius: "0",
                margin: 0,
                fontFamily: "Arial",
                padding: "8px",
                marginTop: "-1px",
                marginLeft: "-1px",
                backgroundColor: backgroundColor,
                color: color,
                opacity: isDisabled ? 0.7 : 1,
                "&:hover": {
                  backgroundColor: isDisabled
                    ? backgroundColor
                    : isSelected
                    ? "#c49b36"
                    : "#f4e4a1",
                },
              }}
            >
              <span style={{ position: 'relative', zIndex: 1 }}>{day.date()}</span>

              {marker && showEventMarkers && !isPast && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: getMarkerStyle(marker),
                    position: "absolute",
                    bottom: 3,
                    right: 3,
                    opacity: isDisabled ? 0.7 : 1,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    border: "1px solid rgba(255,255,255,0.5)",
                  }}
                />
              )}
            </Box>
          );
        })}

        {/* Donor name overlay – inside the grid container so it's positioned correctly */}
        {Object.keys(bookedDetailsMap).length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 1000,
            }}
          >
            {calendarDays.map((day, index) => {
              const dateString = day.format("YYYY-MM-DD");
              const bookingDetail = bookedDetailsMap[dateString];
              if (!bookingDetail) return null;

              const donorName = bookingDetail.donorFirstName;
              const row = Math.floor(index / 7) + 1; // 1-based for data rows
              const col = index % 7;
              const cellSize = 52;

              const weekday = day.day();
              // Define offset from top of cell for each alignment
              const alignments = {
                0: { topOffset: 2, transform: 'translateX(-50%)' },        // Sunday: top
                1: { topOffset: cellSize/2, transform: 'translate(-50%, -50%)' }, // Monday: middle
                2: { topOffset: cellSize - 2, transform: 'translate(-50%, -100%)' }, // Tuesday: bottom (shift up)
                3: { topOffset: 2, transform: 'translateX(-50%)' },
                4: { topOffset: cellSize/2, transform: 'translate(-50%, -50%)' },
                5: { topOffset: cellSize - 2, transform: 'translate(-50%, -100%)' },
                6: { topOffset: cellSize/2, transform: 'translate(-50%, -50%)' },
              };
              const align = alignments[weekday] || alignments[0];

              return (
                <Box
                  key={dateString}
                  sx={{
                    position: "absolute",
                    left: col * cellSize + cellSize / 2,
                    top: row * cellSize + align.topOffset,
                    transform: align.transform,
                    width: "max-content",
                    maxWidth: "150px",
                    backgroundColor: "transparent",
                    color: "#000", // or keep white if you prefer
                    textShadow: "0 0 4px rgba(255,255,255,0.9), 0 0 2px rgba(0,0,0,0.3)",
                    px: 1,
                    borderRadius: "3px",
                    fontSize: "11px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    overflow: "visible",
                    textAlign: "center",
                    pointerEvents: "none",
                    zIndex: 1000,
                  }}
                  title={donorName}
                >
                  {donorName.length > 20 ? donorName.slice(0, 20) + '…' : donorName}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {showLegend && (
        <Box
          mt={2}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={1}
          width="100%"
          sx={{ pl: 1 }}
        >
          <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
            {disableBookedDates && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: "transparent",
                    border: "2px solid #f44336",
                    borderRadius: "2px",
                  }}
                />
                <Typography variant="caption">Booked (Unavailable)</Typography>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#ffebee",
                  border: "1px solid #d32f2f",
                  borderRadius: "2px",
                }}
              />
              <Typography variant="caption">Blocked (Unavailable)</Typography>
            </Box>
            {showEventMarkers && (
              <>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: "#00C853",
                      borderRadius: "50%",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Approved
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: "#FF6D00",
                      borderRadius: "50%",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Pending
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} mt={1}>
            <Typography variant="caption" color="textSecondary">
              📅 Only dates within one year from today can be selected.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});

BookingCalendar.displayName = 'BookingCalendar';

export default BookingCalendar;